/* eslint-disable max-len */
import { CircularProgress, useTheme } from '@material-ui/core';
import { EventLogData } from '@moderepo/mode-apis';
import { ITEMS_PER_PAGE_LIST, ModeTheme, MultiMetricsDataPoint } from '@moderepo/mode-app-components';
import { Entity, EntityClass, EntityModuleType, SmartModuleType, SortOrder, TimeSeriesAggregation } from '@moderepo/mode-internal-apis';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import * as am4charts from '@amcharts/amcharts4/charts';
import { TFunction } from 'i18next';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { EntityNumericMetricsCustomSettings, EntityUIComponentCollection, EntityUIComponentProps, filterMetricsByMetricsExclusionCustomSettings,
    filterMetricsDefinitionsByMetricsExclusionCustomSettings, getMetricsDefinitionMetricsId, isEntityNumericMetricsCustomSettings,
    isFetchEntityTimeRangeDataParams, isMetricsDefinitionAndMetricsExclusionCustomSettings, MetricsDefinitionsAndMetricsExclusionCustomSettings,
    EntityUIComponentAdapterCollection, isFetchEntityTimeSpanDataParams, useSelectTranslatedEntityClasses } from '..';
import { ChartMetricInfo, ChartViewSettings, ExportDataFormat, FetchAggregatedDataOptions, FetchCollectionDataParams, FetchDataNumericMetricParams,
    FetchDataTagMetricParams, FetchEventLogDataParams, FetchRawDataOptions, MetricViewSettings, MultiDataSourcesViewContainer,
    MultiDataSourcesViewContainerProps, GanttChartDataPoint } from '../..';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { useRitzTranslation } from '../../../utils/customHooks';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingNumberValue, isValidSettingStringValue, NoneValue, ReadonlyNonEmptyArray,
    TypeNone } from '../../../utils/utils';
import { createUniqueMetricId } from '.';
import { SettingsErrorLogger } from '../../../utils/logger';
import { MAX_RAW_DATA_COUNT } from '../../time-series/tsUtils';
import { BASIC_XY_CHART_TYPES } from '../../../utils/constants';

/**
 * The interface for the DEFAULT params/info that will be passed to the adapter functions. We will pass as much info as we have in the
 * EntityCombinedMetricsViewUIComponent and let the adapters decide whatever data they need. Note that these are just the basic default
 * params. Each adapter function can pass additional params.
 */
export interface EntityCombinedMetricsViewUIComponentAdapterDefaultParams {
    readonly trans: TFunction;
    readonly theme: ModeTheme;
    readonly entity: Entity;
    readonly entityClass: EntityClass;
    readonly customSettings?: EntityCombinedMetricsViewUIComponentCustomSettings | undefined;
    readonly selectedNumericMetric: ChartMetricInfo | undefined;
    readonly selectedDate: number | undefined,
    readonly numericMetrics?: readonly ChartMetricInfo[] | undefined;
    readonly metricsDataPoints?: readonly MultiMetricsDataPoint[] | undefined,
    readonly tagMetrics?: readonly ChartMetricInfo[] | undefined;
    readonly ganttChartDataPoints?: {
        readonly [metricId: string]: readonly GanttChartDataPoint[] | undefined;
    } | undefined,
}


/**
 * The interface for implementing an Adapter for the EntityCombinedMetricsViewUIComponent.
 * EntityCombinedMetricsViewUIComponent has the default implementation that can handle most cases however, there are some cases where we
 * will need to add some project specific logic. For example, the logic that parse event log data and convert them to data points and
 * tag metrics might be different for some project. Therefore, we will allow the user of this component to provide custom logic to
 * handle these special cases. To implement and use an adapter for the EntityCombinedMetricsViewUIComponent, we need to implement
 * this interface and implement the functions that we need to override. Most of these functions are OPTIONAL and only need to be
 * implemented if we want custom logic for the function.
 */
export interface EntityCombinedMetricsViewUIComponentAdapter {

    /**
     * The callback function to handle processing  event log data. This function is responsible for converting event log records
     * to data point objects that can be used by the Gantt chart. This function is also responsible for dynamically create ChartMetricInfo
     * based on the event logs.
     */
    readonly convertEventLogRecordsToTagChartData?: ((params: {
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    })=> {
        [metricId: string]: GanttChartDataPoint[];
    }) | undefined;

    /**
     * The callback function to handle processing event log data. This function is responsible for dynamically create ChartMetricInfo
     * based on the event logs.
     */
    readonly convertEventLogRecordsToTagMetrics?: ((params: {
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    })=> readonly ChartMetricInfo[]) | undefined;

    /**
     * OPTIONAL function to sort an array of tag metrics and return the sorted array.
     */
    readonly sortTagMetrics?: ((params: EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> readonly ChartMetricInfo[]) | undefined;

    // OPTIONAL handlers for creating custom labels for the XY chart's Y Axis. The default behavior will just display the Y value and unit
    readonly getNumericMetricChartYAxisHTMLLabel?: ((params: {
        readonly text: string | undefined;
        readonly targetAxis: am4charts.AxisLabel;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getNumericMetricChartYAxisTextLabel?: ((params: {
        readonly text: string | undefined;
        readonly targetAxis: am4charts.AxisLabel;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    // OPTIONAL handlers for creating custom labels for the Gantt chart Y Axis. The default behavior will just display the tag metric's displayName
    readonly getTagMetricChartYAxisHTMLLabel?: ((params: {
        readonly text: string | undefined;
        readonly targetAxis: am4charts.AxisLabel;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getTagMetricChartYAxisTextLabel?: ((params: {
        readonly text: string | undefined;
        readonly targetAxis: am4charts.AxisLabel;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getNumericMetricChartTooltipText?: ((params: {
        readonly text: string | undefined;
        readonly targetSeries: am4charts.XYSeries;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getNumericMetricChartTooltipHTML?: ((params: {
        readonly text: string | undefined;
        readonly targetSeries: am4charts.XYSeries;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getTagMetricChartTooltipText?: ((params: {
        readonly text: string | undefined;
        readonly targetSeries: am4charts.XYSeries;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    readonly getTagMetricChartTooltipHTML?: ((params: {
        readonly text: string | undefined;
        readonly targetSeries: am4charts.XYSeries;
        readonly targetMetric: ChartMetricInfo;
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;

    // Get a list of export data formats the handler supports. The handler needs to return an array of ExportDataFormat
    readonly getSupportedExportDataFormats?: ((params: {
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> ReadonlyNonEmptyArray<ExportDataFormat> | undefined) | undefined;

    // Create the content for the data to be exported for the given data format and selected metrics. IMPORTANT NOTE: "export" data is NOT
    // the same as "download" data. This export data feature is for exporting whatever data we are showing on the screen to some other
    // data format. There is no API call for this, it is all done in the frontend.
    readonly createExportFileContent?: ((params: {
        // The format of the data to be exported e.g. JSON, CSV, etc... This format will be one of the formats returned by the getSupportedExportDataFormats function
        readonly selectedDataFormat: string;
        readonly selectedNumericMetrics: readonly ChartMetricInfo[];
        readonly selectedTagMetrics: readonly ChartMetricInfo[];
    } & EntityCombinedMetricsViewUIComponentAdapterDefaultParams)=> string | undefined) | undefined;
}



/**
 * Type guard function to check if an object is a EntityCombinedMetricsViewUIComponent adapter
 */
const isEntityCombinedMetricsViewUIComponentAdapter = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityCombinedMetricsViewUIComponentAdapter => {
    const adapter = obj as EntityCombinedMetricsViewUIComponentAdapter;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(adapter)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : adapter,
            path   : [...configPath],
        }) ?? false;
    }

    // convertEventLogRecordsToTagChartData is OPTIONAL but if it is provided, it must be a function
    if (adapter.convertEventLogRecordsToTagChartData !== undefined) {
        if (typeof adapter.convertEventLogRecordsToTagChartData !== 'function') {
            return errorLogger?.logError({
                message: '"convertEventLogRecordsToTagChartData" must be undefined or a Function.',
                value  : adapter.convertEventLogRecordsToTagChartData,
                path   : [...configPath, 'convertEventLogRecordsToTagChartData'],
            }) ?? false;
        }
    }

    // convertEventLogRecordsToTagMetrics is OPTIONAL but if it is provided, it must be a function
    if (adapter.convertEventLogRecordsToTagMetrics !== undefined) {
        if (typeof adapter.convertEventLogRecordsToTagMetrics !== 'function') {
            return errorLogger?.logError({
                message: '"convertEventLogRecordsToTagMetrics" must be undefined or a Function.',
                value  : adapter.convertEventLogRecordsToTagMetrics,
                path   : [...configPath, 'convertEventLogRecordsToTagMetrics'],
            }) ?? false;
        }
    }

    // sortTagMetrics is OPTIONAL but if it is provided, it must be a function
    if (adapter.sortTagMetrics !== undefined) {
        if (typeof adapter.sortTagMetrics !== 'function') {
            return errorLogger?.logError({
                message: '"sortTagMetrics" must be undefined or a Function.',
                value  : adapter.sortTagMetrics,
                path   : [...configPath, 'sortTagMetrics'],
            }) ?? false;
        }
    }

    // getNumericMetricChartYAxisHTMLLabel is OPTIONAL but if it is provided, it must be a function
    if (adapter.getNumericMetricChartYAxisHTMLLabel !== undefined) {
        if (typeof adapter.getNumericMetricChartYAxisHTMLLabel !== 'function') {
            return errorLogger?.logError({
                message: '"getNumericMetricChartYAxisHTMLLabel" must be undefined or a  Function.',
                value  : adapter.getNumericMetricChartYAxisHTMLLabel,
                path   : [...configPath, 'getNumericMetricChartYAxisHTMLLabel'],
            }) ?? false;
        }
    }

    // getNumericMetricChartYAxisTextLabel is OPTIONAL but if it is provided, it must be a function
    if (adapter.getNumericMetricChartYAxisTextLabel !== undefined) {
        if (typeof adapter.getNumericMetricChartYAxisTextLabel !== 'function') {
            return errorLogger?.logError({
                message: '"getNumericMetricChartYAxisTextLabel" must be undefined or a Function.',
                value  : adapter.getNumericMetricChartYAxisTextLabel,
                path   : [...configPath, 'getNumericMetricChartYAxisTextLabel'],
            }) ?? false;
        }
    }

    // getTagMetricChartYAxisHTMLLabel is OPTIONAL but if it is provided, it must be a function
    if (adapter.getTagMetricChartYAxisHTMLLabel !== undefined) {
        if (typeof adapter.getTagMetricChartYAxisHTMLLabel !== 'function') {
            return errorLogger?.logError({
                message: '"getTagMetricChartYAxisHTMLLabel" must be undefined or a Function.',
                value  : adapter.getTagMetricChartYAxisHTMLLabel,
                path   : [...configPath, 'getTagMetricChartYAxisHTMLLabel'],
            }) ?? false;
        }
    }

    // getTagMetricChartYAxisTextLabel is OPTIONAL but if it is provided, it must be a function
    if (adapter.getTagMetricChartYAxisTextLabel !== undefined) {
        if (typeof adapter.getTagMetricChartYAxisTextLabel !== 'function') {
            return errorLogger?.logError({
                message: '"getTagMetricChartYAxisTextLabel" must be undefined or a a Function.',
                value  : adapter.getTagMetricChartYAxisTextLabel,
                path   : [...configPath, 'getTagMetricChartYAxisTextLabel'],
            }) ?? false;
        }
    }

    // getNumericMetricChartTooltipText is OPTIONAL but if it is provided, it must be a function
    if (adapter.getNumericMetricChartTooltipText !== undefined) {
        if (typeof adapter.getNumericMetricChartTooltipText !== 'function') {
            return errorLogger?.logError({
                message: '"getNumericMetricChartTooltipText" must be undefined or a Function.',
                value  : adapter.getNumericMetricChartTooltipText,
                path   : [...configPath, 'getNumericMetricChartTooltipText'],
            }) ?? false;
        }
    }

    // getNumericMetricChartTooltipHTML is OPTIONAL but if it is provided, it must be a function
    if (adapter.getNumericMetricChartTooltipHTML !== undefined) {
        if (typeof adapter.getNumericMetricChartTooltipHTML !== 'function') {
            return errorLogger?.logError({
                message: '"getNumericMetricChartTooltipHTML" must be undefined or a Function.',
                value  : adapter.getNumericMetricChartTooltipHTML,
                path   : [...configPath, 'getNumericMetricChartTooltipHTML'],
            }) ?? false;
        }
    }

    // getTagMetricChartTooltipText is OPTIONAL but if it is provided, it must be a function
    if (adapter.getTagMetricChartTooltipText !== undefined) {
        if (typeof adapter.getTagMetricChartTooltipText !== 'function') {
            return errorLogger?.logError({
                message: '"getTagMetricChartTooltipText" must be undefined or a Function.',
                value  : adapter.getTagMetricChartTooltipText,
                path   : [...configPath, 'getTagMetricChartTooltipText'],
            }) ?? false;
        }
    }

    // getTagMetricChartTooltipHTML is OPTIONAL but if it is provided, it must be a function
    if (adapter.getTagMetricChartTooltipHTML !== undefined) {
        if (typeof adapter.getTagMetricChartTooltipHTML !== 'function') {
            return errorLogger?.logError({
                message: '"getTagMetricChartTooltipHTML" must be undefined or a Function.',
                value  : adapter.getTagMetricChartTooltipHTML,
                path   : [...configPath, 'getTagMetricChartTooltipHTML'],
            }) ?? false;
        }
    }

    // getSupportedExportDataFormats is OPTIONAL but if it is provided, it must be a function
    if (adapter.getSupportedExportDataFormats !== undefined) {
        if (typeof adapter.getSupportedExportDataFormats !== 'function') {
            return errorLogger?.logError({
                message: '"getSupportedExportDataFormats" must be undefined or a Function.',
                value  : adapter.getSupportedExportDataFormats,
                path   : [...configPath, 'getSupportedExportDataFormats'],
            }) ?? false;
        }
    }

    // createExportFileContent is OPTIONAL but if it is provided, it must be a function
    if (adapter.createExportFileContent !== undefined) {
        if (typeof adapter.createExportFileContent !== 'function') {
            return errorLogger?.logError({
                message: '"createExportFileContent" must be undefined or a Function.',
                value  : adapter.createExportFileContent,
                path   : [...configPath, 'createExportFileContent'],
            }) ?? false;
        }
    }

    return true;
};



/**
 * Event logs data are project specific therefore the frontend doesn't know how to use them. These settings are needed so that the
 * frontend knows what to do with the data. This is only needed is an entity has "home_app_proxy" metric definition module type.
 */
export interface EventLogDataViewSettings {
    // Events are grouped by some ID. An event log contains many fields and some of them are used for composing unique event group ID. This
    // array specifies which fields are needed to compose unique event group id. For example, if a group ID is consisted of fields
    // ['block', 'holeNo', 'stage'], then all events that have the same values for these 3 fields will belonged to the same group.
    readonly groupIdCompositionFields: readonly string[];

    // The field that contains the value for the event name. Since the log record is project specific, frontend doesn't know which
    // field from the record contain the value for the event. This prop tells the frontend which field to look at.
    readonly eventNameField: string;

    // All log events will have start/end events. We will not assume the value for these event will be 'start' and 'end' since
    // different project can use different value e.g. 'begin' and 'end', 'start' and 'stop', etc... So use these 2 props to tell the
    // frontend which value will be used as start event and which value will be used as stop event.
    readonly startEventValue: string;
    readonly endEventValue: string;

    // The field that contain the value for the event
    readonly eventValueField: string;

    // The map of eventValue => color string. This is needed to color the bar. If this is not specified, a default color is used and
    // all the bar will have the same colors which might not be easy to look at
    readonly colorsByEventValue?: {
        readonly [eventValue: string]: string | undefined;
    } | undefined;
}


/**
 * type guard function to check if an object is a type of EventLogDataViewSettings
 */
const isEventLogDataViewSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EventLogDataViewSettings => {
    const settings = obj as EventLogDataViewSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // Make sure groupIdCompositionFields is an array of non-empty strings with at least 1 item
    if (
        settings.groupIdCompositionFields === undefined
        || !(settings.groupIdCompositionFields instanceof Array)
        || settings.groupIdCompositionFields.length === 0
    ) {
        return errorLogger?.logError({
            message: '"groupIdCompositionFields" is required and must be a non-empty Array',
            value  : settings.groupIdCompositionFields,
            path   : [...configPath, 'groupIdCompositionFields'],
        }) ?? false;
    }

    // If one of the items is not a non-empty string show error
    if (!settings.groupIdCompositionFields.every((value, index) => {
        if (!isValidSettingStringValue(value, true)) {
            return errorLogger?.logError({
                message: 'All items in "groupIdCompositionFields" must be non-empty strings',
                value,
                path   : [...configPath, 'groupIdCompositionFields', `[${index}]`],
            }) ?? false;
        }
        return true;
    })) {
        return false;
    }

    // Make sure eventNameField is a non-empty string
    if (!isValidSettingStringValue(settings.eventNameField, true)) {
        return errorLogger?.logError({
            message: '"eventNameField" is required and must be a non-empty string',
            value  : settings.eventNameField,
            path   : [...configPath, 'eventNameField'],
        }) ?? false;
    }

    // Make sure startEventValue is a non-empty string
    if (!isValidSettingStringValue(settings.startEventValue, true)) {
        return errorLogger?.logError({
            message: '"startEventValue" is required and must be a non-empty string',
            value  : settings.startEventValue,
            path   : [...configPath, 'startEventValue'],
        }) ?? false;
    }

    // Make sure endEventValue is a non-empty string
    if (!isValidSettingStringValue(settings.endEventValue, true)) {
        return errorLogger?.logError({
            message: '"endEventValue" is required and must be a non-empty string',
            value  : settings.endEventValue,
            path   : [...configPath, 'endEventValue'],
        }) ?? false;
    }

    // Make sure eventValueField is a non-empty string
    if (!isValidSettingStringValue(settings.eventValueField, true)) {
        return errorLogger?.logError({
            message: '"eventValueField" is required and must be a non-empty string',
            value  : settings.eventValueField,
            path   : [...configPath, 'eventValueField'],
        }) ?? false;
    }

    // colorsByEventValue can be undefined or an Object. If it is an object make sure it is a valid object
    if (settings.colorsByEventValue !== undefined) {

        // Make sure colorsByEventValue is an Object
        if (!isObjectLiteral(settings.colorsByEventValue)) {
            return errorLogger?.logError({
                message: '"colorsByEventValue" must be undefined or an Object',
                value  : settings.colorsByEventValue,
                path   : [...configPath, 'colorsByEventValue'],
            }) ?? false;
        }

        // Make sure ALL values in colorsByEventValue map are of type string and they are not empty strings
        if (!Object.entries(settings.colorsByEventValue).every(([eventValue, colorString]) => {
            if (!isValidSettingStringValue(colorString, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "colorsByEventValue" must be non-empty strings',
                    value  : colorString,
                    path   : [...configPath, 'colorsByEventValue', eventValue],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    return true;
};



/**
 * Type guard function to check if an object is a MetricViewSetting
 */
const isMetricViewSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is MetricViewSettings => {
    const settings = obj as MetricViewSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showLegends)) {
        return errorLogger?.logError({
            message: '"showLegends" must be undefined or a boolean',
            value  : settings.showLegends,
            path   : [...configPath, 'showLegends'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.maxLegends, false, false, 1)) {
        return errorLogger?.logError({
            message: '"maxLegends" must be undefined or an integer greater than 0',
            value  : settings.maxLegends,
            path   : [...configPath, 'maxLegends'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.maxInitiallySelected, false, false, 1)) {
        return errorLogger?.logError({
            message: '"maxInitiallySelected" must be undefined or an integer greater than 0',
            value  : settings.maxInitiallySelected,
            path   : [...configPath, 'maxInitiallySelected'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.allowUnselect)) {
        return errorLogger?.logError({
            message: '"allowUnselect" must be undefined or a boolean',
            value  : settings.allowUnselect,
            path   : [...configPath, 'allowUnselect'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.hideMetricsWithoutData)) {
        return errorLogger?.logError({
            message: '"hideMetricsWithoutData" must be undefined or a boolean',
            value  : settings.hideMetricsWithoutData,
            path   : [...configPath, 'hideMetricsWithoutData'],
        }) ?? false;
    }

    return true;
};



/**
 * The structure for customizing the table view
 */
export interface TableViewSettings {
    // The order of display data. The default is "asc".
    readonly sortOrder?: SortOrder | undefined;

    // The initial items per page value to use for the table pagination
    readonly defaultPageSize?: number | undefined;
}


/**
 * Type guard function to check if an object is a TableViewSettings
 */
const isTableViewSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is TableViewSettings => {
    const settings = obj as EntityCombinedMetricsViewUIComponentCustomSettings['tableViewSettings'];
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.sortOrder, false, false, Object.values(SortOrder))) {
        return errorLogger?.logError({
            message: `"sortOrder" must be undefined or one of the following values [${Object.values(SortOrder).map((value) => {
                return `"${value}"`;
            }).join(', ')}].`,
            value: settings.sortOrder,
            path : [...configPath, 'sortOrder'],
        }) ?? false;
    }

    // Make sure defaultPageSize is one of the predefined page sizes if it is provided e.g. 5, 10, 20, 50, 100
    if (
        settings.defaultPageSize !== undefined
        && (!isValidSettingNumberValue(settings.defaultPageSize, false, false) || !ITEMS_PER_PAGE_LIST.includes(settings.defaultPageSize))
    ) {
        return errorLogger?.logError({
            message: `"defaultPageSize" must be undefined or one the following numbers [${Object.values(
                ITEMS_PER_PAGE_LIST,
            ).map((pageSize) => {
                return `${pageSize}`;
            }).join(', ')}].`,
            value: settings.defaultPageSize,
            path : [...configPath, 'defaultPageSize'],
        }) ?? false;
    }

    return true;
};



/**
 * Interface for the EntityCombinedMetricsViewUIComponent's customSettings. Because 'EntityCombinedMetricsViewUIComponent' displays the
 * entity's metrics, we can have it extends the MetricsExclusionCustomSettings which comes with the options for excluding/including
 * metrics. We don't need to define those.
 */
export interface EntityCombinedMetricsViewUIComponentCustomSettings extends MetricsDefinitionsAndMetricsExclusionCustomSettings, EntityNumericMetricsCustomSettings {

    // Event logs data are project specific therefore the frontend doesn't know how to use them. These settings are needed so that the
    // frontend knows what to do with the data. This is only needed is an entity has "home_app_proxy" metric definition module type.
    // And these view settings need to be configured PER metric definition because each metric definition can have totally different
    // event record structure.
    readonly eventLogDataViewSettingsPerMetricsDefinition?: {
        readonly [metricsDefinitionId: string]: EventLogDataViewSettings | undefined;
    } | undefined;

    // The name of the adapter to use. This adapter will handle project specific logic such as event log data parser and sorting metrics
    readonly adapter?: string;

    // The icon to display in the component header
    readonly icon?: string | undefined;

    // The title to display in the component header
    // TODO - Remove this once all project start using the "titleKey" settings
    readonly title?: string | undefined;

    // The title to display in the component header
    readonly titleKey?: string | undefined;

    // Customization for the chart's view
    readonly chartViewSettings?: ChartViewSettings | undefined;

    // Customization for the table view
    readonly tableViewSettings?: TableViewSettings | undefined;

    // Whether or not to show the CHART view. The default is TRUE unless this is specifically set to false
    readonly showChartView?: boolean | undefined;

    // Whether or not to show TABLE view. The default is FALSE unless this is specifically set to true
    readonly showTableView?: boolean | undefined;

    // Customization for the how we display the metrics
    readonly metricViewSettings?: {
        readonly numericMetrics?: MetricViewSettings | undefined;
        readonly tagMetrics?: MetricViewSettings | undefined;
    } | undefined;

    // The option show vertical line where the user clicked on.
    readonly displayVerticalLineWhereClicked?: boolean | undefined;

    // The option highlight the selected metrics.
    readonly highlightSelectedMetrics?: boolean | undefined;

    // The aggregation to use for fetching TSDB data. If this is configured then the component will use this aggregation if there is no other
    // aggregation specified e.g. aggregation specified from the entity view or aggregation specified in the metrics definition's 'evaluator'.
    readonly aggregation?: TimeSeriesAggregation | TypeNone | undefined;

    // If aggregation is "none" which mean the component will fetch "raw" data. For fetching raw data, back end has a limit of 500 and that will
    // be the default limit the component will use. The admin can set a different limit using this option. The value can be any number between
    // -500 and 500 inclusive. To fetch data starting from the "endTime", set this value to a negative number. To fetch data starting from
    // the startTime, set this value to a positive number
    readonly fetchRawDataLimit?: number | undefined;

    // TODO - Add aggregation settings for each metrics definition so that we can use different aggregation for different collection.
    /**
    readonly aggregationPerMetricsDefinition?: {
        readonly [metricsDefinitionId: string]: TimeSeriesAggregation | undefined;
    } | undefined;
    */

    // To specify the list of metrics to be selected initially by default. This settings will override the "metricViewSettings.maxInitiallySelected"
    // settings. For example: if "maxInitiallySelected" is set to 4 but if the number of preselected metrics is 5, all 5 metrics specified in this
    // config will be shown. However, if the number of preselect metrics is 3 then these 3 metrics will guarantee to be shown and then 1 other
    // metrics will be shown as well to make it 4 total.
    // Basically, this settings tell the component to always show these metrics initially.
    readonly preselectMetricsPerMetricsDefinition?: {
        readonly [metricsDefinitionId: string]: {
            readonly numericMetrics: readonly string[] | undefined;
            readonly tagMetrics: readonly string[] | undefined;
        } | undefined;
    } | undefined;

    readonly styles?: MultiDataSourcesViewContainerProps['customStyles'] | undefined;
}


const isEntityCombinedMetricsViewUIComponentCustomChartViewSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is ChartViewSettings => {
    const settings = obj as EntityCombinedMetricsViewUIComponentCustomSettings['chartViewSettings'];
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // verticalLineStyle is optional but if it is defined, it must be an object
    if (settings.verticalLineStyle !== undefined) {

        if (!isObjectLiteral(settings.verticalLineStyle)) {
            return errorLogger?.logError({
                message: '"verticalLineStyle" must be undefined or an Object',
                value  : settings.verticalLineStyle,
                path   : [...configPath, 'verticalLineStyle'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.verticalLineStyle.color)) {
            return errorLogger?.logError({
                message: '"color" must be undefined or a non-empty string.',
                value  : settings.verticalLineStyle.color,
                path   : [...configPath, 'verticalLineStyle', 'color'],
            }) ?? false;
        }

        if (!isValidSettingNumberValue(settings.verticalLineStyle.width, false, false, 1, 10)) {
            return errorLogger?.logError({
                message: '"width" must be undefined or an integer between 1 and 10 inclusive.',
                value  : settings.verticalLineStyle.width,
                path   : [...configPath, 'verticalLineStyle', 'width'],
            }) ?? false;
        }

        if (!isValidSettingNumberValue(settings.verticalLineStyle.opacity, false, true, 0, 1)) {
            return errorLogger?.logError({
                message: '"opacity" must be undefined or a decimal number between 0 and 1.',
                value  : settings.verticalLineStyle.opacity,
                path   : [...configPath, 'verticalLineStyle', 'opacity'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.verticalLineStyle.strokeDasharray)) {
            return errorLogger?.logError({
                message: '"strokeDasharray" must be undefined or a non-empty string.',
                value  : settings.verticalLineStyle.strokeDasharray,
                path   : [...configPath, 'verticalLineStyle', 'strokeDasharray'],
            }) ?? false;
        }
    }


    const supportedCursorBehaviors = ['zoomX', 'zoomY', 'zoomXY', 'selectX', 'selectY', 'selectXY', 'panX', 'panY', 'panXY', 'none'];

    // Validate "numericChartSettings", the settings for the numeric chart which show numeric data
    if (settings.numericChartSettings !== undefined) {
        if (!isObjectLiteral(settings.numericChartSettings)) {
            return errorLogger?.logError({
                message: '"numericChartSettings" must be undefined or an object.',
                value  : settings.numericChartSettings,
                path   : [...configPath, 'numericChartSettings'],
            }) ?? false;
        }

        // selectedMetricStyle is optional but if it is defined, it must be an object
        if (settings.numericChartSettings.selectedMetricStyle !== undefined) {
            if (!isObjectLiteral(settings.numericChartSettings.selectedMetricStyle)) {
                return errorLogger?.logError({
                    message: '"selectedMetricStyle" must be undefined or an Object.',
                    value  : settings.numericChartSettings.selectedMetricStyle,
                    path   : [...configPath, 'numericChartSettings', 'selectedMetricStyle'],
                }) ?? false;
            }

            if (!isValidSettingNumberValue(settings.numericChartSettings.selectedMetricStyle.width, false, false, 1, 10)) {
                return errorLogger?.logError({
                    message: '"width" must be undefined or an integer between 1 and 10 inclusive.',
                    value  : settings.numericChartSettings.selectedMetricStyle.width,
                    path   : [...configPath, 'numericChartSettings', 'selectedMetricStyle', 'width'],
                }) ?? false;
            }
        }

        if (!isValidSettingBooleanValue(settings.numericChartSettings.shareYAxis)) {
            return errorLogger?.logError({
                message: '"shareYAxis" must be undefined or a boolean.',
                value  : settings.numericChartSettings.shareYAxis,
                path   : [...configPath, 'numericChartSettings', 'shareYAxis'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.numericChartSettings.syncYAxes)) {
            return errorLogger?.logError({
                message: '"syncYAxes" must be undefined or a boolean.',
                value  : settings.numericChartSettings.syncYAxes,
                path   : [...configPath, 'numericChartSettings', 'syncYAxes'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.numericChartSettings.chartType, false, false, BASIC_XY_CHART_TYPES.map((type) => {
            return type as string;
        }))) {
            return errorLogger?.logError({
                message: `"chartType" must be undefined or one of the following [${
                    Object.values(BASIC_XY_CHART_TYPES).map((chartType) => {
                        return `"${chartType}"`;
                    }).join(', ')
                }].`,
                value: settings.numericChartSettings.chartType,
                path : [...configPath, 'numericChartSettings', 'chartType'],
            }) ?? false;
        }

        if (settings.numericChartSettings.yAxisRange !== undefined) {
            if (!isObjectLiteral(settings.numericChartSettings.yAxisRange)) {
                return errorLogger?.logError({
                    message: '"yAxisRange" must be undefined or an object.',
                    value  : settings.numericChartSettings.yAxisRange,
                    path   : [...configPath, 'numericChartSettings', 'yAxisRange'],
                }) ?? false;
            }

            if (!isValidSettingNumberValue(settings.numericChartSettings.yAxisRange.min, false, true)) {
                return errorLogger?.logError({
                    message: '"min" must be undefined or a number.',
                    value  : settings.numericChartSettings.yAxisRange.min,
                    path   : [...configPath, 'numericChartSettings', 'yAxisRange', 'min'],
                }) ?? false;
            }

            if (!isValidSettingNumberValue(settings.numericChartSettings.yAxisRange.max, false, true)) {
                return errorLogger?.logError({
                    message: '"max" must be undefined or a number.',
                    value  : settings.numericChartSettings.yAxisRange.max,
                    path   : [...configPath, 'numericChartSettings', 'yAxisRange', 'max'],
                }) ?? false;
            }

            if (
                settings.numericChartSettings.yAxisRange.min !== undefined
                && settings.numericChartSettings.yAxisRange.max !== undefined
                && settings.numericChartSettings.yAxisRange.max <= settings.numericChartSettings.yAxisRange.min
            ) {
                return errorLogger?.logError({
                    message: '"max" must be undefined or a number greater than min.',
                    value  : settings.numericChartSettings.yAxisRange.max,
                    path   : [...configPath, 'numericChartSettings', 'yAxisRange', 'max'],
                }) ?? false;
            }
        }

        if (!isValidSettingStringValue(settings.numericChartSettings.cursorBehavior, false, false, supportedCursorBehaviors)) {
            return errorLogger?.logError({
                message: `"cursorBehaviors" must be undefined or one of the following [${
                    Object.values(supportedCursorBehaviors).map((cursorBehavior) => {
                        return `"${cursorBehavior}"`;
                    }).join(', ')
                }].`,
                value: settings.numericChartSettings.cursorBehavior,
                path : [...configPath, 'numericChartSettings', 'cursorBehavior'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.numericChartSettings.showHorizontalControlBar)) {
            return errorLogger?.logError({
                message: '"showHorizontalControlBar" must be undefined or a boolean.',
                value  : settings.numericChartSettings.showHorizontalControlBar,
                path   : [...configPath, 'numericChartSettings', 'showHorizontalControlBar'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.numericChartSettings.showVerticalControlBar)) {
            return errorLogger?.logError({
                message: '"showVerticalControlBar" must be undefined or a boolean.',
                value  : settings.numericChartSettings.showVerticalControlBar,
                path   : [...configPath, 'numericChartSettings', 'showVerticalControlBar'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.numericChartSettings.groupYAxesBy, false, false, ['unit', 'group_name'])) {
            return errorLogger?.logError({
                message: '"groupYAxesBy" must be undefined or a string with value "unit" or "group_name".',
                value  : settings.numericChartSettings.groupYAxesBy,
                path   : [...configPath, 'numericChartSettings', 'groupYAxesBy'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.numericChartSettings.connectDataGap)) {
            return errorLogger?.logError({
                message: '"connectDataGap" must be undefined or a boolean.',
                value  : settings.numericChartSettings.connectDataGap,
                path   : [...configPath, 'numericChartSettings', 'connectDataGap'],
            }) ?? false;
        }
    }


    // Validate "tagChartSettings", the settings for the Gantt chart which show tag data
    if (settings.tagChartSettings !== undefined) {
        if (!isObjectLiteral(settings.tagChartSettings)) {
            return errorLogger?.logError({
                message: '"tagChartSettings" must be undefined or an object.',
                value  : settings.tagChartSettings,
                path   : [...configPath, 'tagChartSettings'],
            }) ?? false;
        }

        if (!isValidSettingStringValue(settings.tagChartSettings.cursorBehavior, false, false, supportedCursorBehaviors)) {
            return errorLogger?.logError({
                message: `"cursorBehaviors" must be undefined or one of the following [${
                    Object.values(supportedCursorBehaviors).map((cursorBehavior) => {
                        return `"${cursorBehavior}"`;
                    }).join(', ')
                }].`,
                value: settings.tagChartSettings.cursorBehavior,
                path : [...configPath, 'tagChartSettings', 'cursorBehavior'],
            }) ?? false;
        }

        if (!isValidSettingBooleanValue(settings.tagChartSettings.showHorizontalControlBar)) {
            return errorLogger?.logError({
                message: '"showHorizontalControlBar" must be undefined or a boolean.',
                value  : settings.tagChartSettings.showHorizontalControlBar,
                path   : [...configPath, 'tagChartSettings', 'showHorizontalControlBar'],
            }) ?? false;
        }
    }

    return true;
};



/**
 * type guard function to check if an object is a type of EntityCombinedMetricsViewUIComponentCustomSettings
 */
const isEntityCombinedMetricsViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityCombinedMetricsViewUIComponentCustomSettings => {
    const settings = obj as EntityCombinedMetricsViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    // eventLogDataViewSettingsPerMetricsDefinition can be undefined or an object. But if is an object, it must contain the required props
    if (settings.eventLogDataViewSettingsPerMetricsDefinition !== undefined) {

        // Make sure "eventLogDataViewSettingsPerMetricsDefinition" is an object
        if (!isObjectLiteral(settings.eventLogDataViewSettingsPerMetricsDefinition)) {
            return errorLogger?.logError({
                message: '"eventLogDataViewSettingsPerMetricsDefinition" must be undefined or an Object.',
                value  : settings.eventLogDataViewSettingsPerMetricsDefinition,
                path   : [...configPath, 'eventLogDataViewSettingsPerMetricsDefinition'],
            }) ?? false;
        }

        // Check EACH eventLogDataViewSetting per metricDefinition. If SOME eventLogDataViewSettingsPerMetricsDefinition DOES NOT pass the
        // type guard check then return error
        if (!Object.entries(settings.eventLogDataViewSettingsPerMetricsDefinition).every(([metricsDefId, eventLogDataViewSetting]) => {
            return isEventLogDataViewSettings(
                eventLogDataViewSetting,
                errorLogger,
                [...configPath, 'eventLogDataViewSettingsPerMetricsDefinition', metricsDefId],
            );
        })) {
            return false;
        }
    }


    // "adapter" can be undefined or the name of one of the existing adapter
    if (settings.adapter !== undefined) {
        if (!isValidSettingStringValue(settings.adapter, true)) {
            return errorLogger?.logError({
                message: '"adapter" must be undefined or a non-empty string.',
                value  : settings.adapter,
                path   : [...configPath, 'adapter'],
            }) ?? false;
        }

        // Now get the adapter object from the adapter collection and make sure it is valid
        const adapter = EntityUIComponentAdapterCollection.getInstance().getAdapter(settings.adapter);
        if (!adapter) {
            return errorLogger?.logError({
                message: '"adapter" does not exist.',
                value  : settings.adapter,
                path   : [...configPath, 'adapter'],
            }) ?? false;
        }

        if (!isEntityCombinedMetricsViewUIComponentAdapter(adapter, errorLogger, [...configPath, 'adapter'])) {
            return false;
        }
    }

    // displayVerticalLine is optional but if it is set, it must be a boolean
    if (!isValidSettingBooleanValue(settings.displayVerticalLineWhereClicked)) {
        return errorLogger?.logError({
            message: '"displayVerticalLineWhereClicked" must be undefined or a boolean.',
            value  : settings.displayVerticalLineWhereClicked,
            path   : [...configPath, 'displayVerticalLineWhereClicked'],
        }) ?? false;
    }

    // highlightSelectedMetrics is optional but if it is set, it must be a boolean
    if (!isValidSettingBooleanValue(settings.highlightSelectedMetrics)) {
        return errorLogger?.logError({
            message: '"highlightSelectedMetrics" must be undefined or a boolean.',
            value  : settings.highlightSelectedMetrics,
            path   : [...configPath, 'highlightSelectedMetrics'],
        }) ?? false;
    }

    // icon can be undefined or a string
    if (!isValidSettingStringValue(settings.icon)) {
        return errorLogger?.logError({
            message: '"icon" must be undefined or a non-empty string.',
            value  : settings.icon,
            path   : [...configPath, 'icon'],
        }) ?? false;
    }

    // title can be undefined or a string
    if (!isValidSettingStringValue(settings.title)) {
        return errorLogger?.logError({
            message: '"title" must be undefined or a non-empty string.',
            value  : settings.title,
            path   : [...configPath, 'title'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.titleKey)) {
        return errorLogger?.logError({
            message: '"titleKey" must be undefined or a non-empty string.',
            value  : settings.titleKey,
            path   : [...configPath, 'titleKey'],
        }) ?? false;
    }

    // chartSettings can be undefined or an Object. If it is an object, it must have the correct chart settings
    if (settings.chartViewSettings !== undefined) {
        if (!isEntityCombinedMetricsViewUIComponentCustomChartViewSettings(
            settings.chartViewSettings, errorLogger, [...configPath, 'chartViewSettings'],
        )) {
            return false;
        }
    }

    // tableViewSettings can be undefined or an Object. If it is an object, it must have the correct table view settings
    if (settings.tableViewSettings !== undefined) {
        if (!isTableViewSettings(
            settings.tableViewSettings, errorLogger, [...configPath, 'tableViewSettings'],
        )) {
            return false;
        }
    }

    if (!isValidSettingBooleanValue(settings.showChartView)) {
        return errorLogger?.logError({
            message: '"showChartView" must be undefined or a boolean.',
            value  : settings.showChartView,
            path   : [...configPath, 'showChartView'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showTableView)) {
        return errorLogger?.logError({
            message: '"showTableView" must be undefined or a boolean.',
            value  : settings.showTableView,
            path   : [...configPath, 'showTableView'],
        }) ?? false;
    }

    // Make sure not BOTH showChartView and showTableView are false
    // NOTE: "showChartView" is true by default so need to check if it is specifically set to "false".
    if (settings.showChartView === false && !settings.showTableView) {
        return errorLogger?.logError({
            message: 'Either one "showChartView" or "showTableView" must be set to true.',
            value  : settings.showChartView,
            path   : [...configPath, 'showChartView'],
        }) ?? false;
    }

    // metricViewSettings can be undefined or an Object.
    if (settings.metricViewSettings !== undefined) {

        // metricViewSettings must be an object
        if (!isObjectLiteral(settings.metricViewSettings)) {
            return errorLogger?.logError({
                message: '"metricViewSettings" must be undefined or an Object.',
                value  : settings.metricViewSettings,
                path   : [...configPath, 'metricViewSettings'],
            }) ?? false;
        }

        // metricViewSettings.numericMetrics can be undefined or an object of type MetricViewSettings
        if (settings.metricViewSettings.numericMetrics !== undefined && !isMetricViewSettings(
            settings.metricViewSettings.numericMetrics, errorLogger, [...configPath, 'metricViewSettings', 'numericMetrics'],
        )) {
            return false;
        }

        // metricViewSettings.tagMetrics can be undefined or an object of type MetricViewSettings
        if (settings.metricViewSettings.tagMetrics !== undefined && !isMetricViewSettings(
            settings.metricViewSettings.tagMetrics, errorLogger, [...configPath, 'metricViewSettings', 'tagMetrics'],
        )) {
            return false;
        }
    }


    const supportedAggregations = [...Object.values(TimeSeriesAggregation), NoneValue];

    // aggregation and defaultAggregation is optional but if it is provided, it must be one of the TimeSeriesAggregation
    if (!isValidSettingStringValue(settings.aggregation, false, false, supportedAggregations)) {
        return errorLogger?.logError({
            message: `"aggregation" must be undefined or one of the following values [${
                supportedAggregations.map((type) => {
                    return `"${type}"`;
                }).join(', ')}]`,
            value: settings.aggregation,
            path : [...configPath, 'aggregation'],
        }) ?? false;
    }

    if (!isValidSettingNumberValue(settings.fetchRawDataLimit, false, false, -MAX_RAW_DATA_COUNT, MAX_RAW_DATA_COUNT)) {
        return errorLogger?.logError({
            message: `"fetchRawDataLimit" must be undefined or an integer between ${-MAX_RAW_DATA_COUNT} and ${MAX_RAW_DATA_COUNT}.`,
            value  : settings.fetchRawDataLimit,
            path   : [...configPath, 'fetchRawDataLimit'],
        }) ?? false;
    }

    if (settings.fetchRawDataLimit === 0) {
        return errorLogger?.logError({
            message: '"fetchRawDataLimit" cannot be 0.',
            value  : settings.fetchRawDataLimit,
            path   : [...configPath, 'fetchRawDataLimit'],
        }) ?? false;
    }

    // "preselectMetricsPerMetricsDefinition" is optional but if it is provided then it must be valid
    if (settings.preselectMetricsPerMetricsDefinition !== undefined) {
        if (!isObjectLiteral(settings.preselectMetricsPerMetricsDefinition)) {
            return errorLogger?.logError({
                message: '"preselectMetricsPerMetricsDefinition" must be undefined or an Object.',
                value  : settings.preselectMetricsPerMetricsDefinition,
                path   : [...configPath, 'preselectMetricsPerMetricsDefinition'],
            }) ?? false;
        }

        if (!Object.entries(settings.preselectMetricsPerMetricsDefinition).every(([metricsDefId, metricsDefSettings]) => {
            // "metricsDefSettings" can have "numericMetrics" and "tagMetrics" and they are optional but if they are provided, they must be
            // Arrays of non-empty strings
            if (metricsDefSettings !== undefined) {

                if (!isObjectLiteral(metricsDefSettings)) {
                    return errorLogger?.logError({
                        message: `"${metricsDefId}" must be an Object.`,
                        value  : metricsDefSettings,
                        path   : [...configPath, 'preselectMetricsPerMetricsDefinition', `${metricsDefId}`],
                    }) ?? false;
                }

                // Make sure "numericMetrics" is an Array if none empty strings e.g. numericMetrics = ["temperature", "pressure"]
                if (metricsDefSettings.numericMetrics !== undefined) {
                    if (!(metricsDefSettings.numericMetrics instanceof Array)) {
                        return errorLogger?.logError({
                            message: '"numericMetrics" must be undefined or an Array of non-empty strings.',
                            value  : metricsDefSettings.numericMetrics,
                            path   : [...configPath, 'preselectMetricsPerMetricsDefinition', `${metricsDefId}`, 'numericMetrics'],
                        }) ?? false;
                    }
                    if (!metricsDefSettings.numericMetrics.every((metricName, index) => {
                        if (!isValidSettingStringValue(metricName, true)) {
                            return errorLogger?.logError({
                                message: 'Value must be non-empty strings.',
                                value  : metricName,
                                path   : [...configPath, 'preselectMetricsPerMetricsDefinition', `${metricsDefId}`, 'numericMetrics', `[${index}]`],
                            }) ?? false;
                        }
                        return true;
                    })) {
                        return false;
                    }
                }

                // Make sure "tagMetrics" is an Array if none empty strings e.g. tagMetrics = ["error", "stage"]
                if (metricsDefSettings.tagMetrics !== undefined) {
                    if (!(metricsDefSettings.tagMetrics instanceof Array)) {
                        return errorLogger?.logError({
                            message: '"tagMetrics" must be undefined or an Array of non-empty strings.',
                            value  : metricsDefSettings.tagMetrics,
                            path   : [...configPath, 'preselectMetricsPerMetricsDefinition', `${metricsDefId}`, 'tagMetrics'],
                        }) ?? false;
                    }
                    if (!metricsDefSettings.tagMetrics.every((metricName, index) => {
                        if (!isValidSettingStringValue(metricName, true)) {
                            return errorLogger?.logError({
                                message: 'Value must be non-empty strings.',
                                value  : metricName,
                                path   : [...configPath, 'preselectMetricsPerMetricsDefinition', `${metricsDefId}`, 'tagMetrics', `[${index}]`],
                            }) ?? false;
                        }
                        return true;
                    })) {
                        return false;
                    }
                }
            }
            return true;
        })) {
            return false;
        }
    }


    // Because settings is also an MetricsExclusionCustomSettings, we can call this type guard function to validate the props defined
    // for "MetricsExclusionCustomSettings"
    if (!isMetricsDefinitionAndMetricsExclusionCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // Because settings is also an EntityNumericMetricsCustomSettings, we can call this type guard function to validate the props defined
    // for "EntityNumericMetricsCustomSettings"
    if (!isEntityNumericMetricsCustomSettings(settings, errorLogger, [...configPath])) {
        return false;
    }

    // "styles" is optional but if they are provided, they must be objects
    if (settings.styles !== undefined) {
        if (!isObjectLiteral(settings.styles)) {
            return errorLogger?.logError({
                message: '"styles" must be undefined or an Object.',
                value  : settings.styles,
                path   : [...configPath, 'styles'],
            }) ?? false;
        }

        // All values in the styles object must be valid CSS objects
        if (!Object.entries(settings.styles).every(([key, value]) => {
            if (!isObjectLiteral(value)) {
                return errorLogger?.logError({
                    message: `${key} must be undefined or a CSSProperties object.`,
                    value,
                    path   : [...configPath, 'styles', `${key}`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    return true;
};



export interface EntityCombinedMetricsViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityCombinedMetricsViewUIComponentCustomSettings | undefined;
}


/**
 * Similar to the EntityMetricsViewUIComponent, this component is also used for displaying all metrics belonged to an entity. However, this
 * component will only use 1 chart to display all the metrics.
 */
export const EntityCombinedMetricsViewUIComponent: React.FC<EntityCombinedMetricsViewUIComponentProps> = (
    props: EntityCombinedMetricsViewUIComponentProps,
) => {

    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);
    const theme = useTheme() as ModeTheme;


    // Give the component a unique ID. We need to prepend this ID to the chart element's ID to make sure the chart element ID is unique.
    const componentIdRef = useRef<string>(uuidv4());

    // Get the entity object for the specified entityId
    const selectedEntity = useSelectEntity(props.projectId, props.entityId);

    // Get the entity class linked to the selected entity instance but first we need to load all the entity classes first
    const entityClasses = useSelectTranslatedEntityClasses(props.projectId, trans);
    const selectedEntityClass = useMemo((): EntityClass | undefined => {
        if (selectedEntity && entityClasses) {
            // find the entityClass for the entity instance because we need to get entityClass's metricsDefinition.
            return entityClasses.find((ec) => {
                return ec.entityClass === selectedEntity.entityClass;
            });
        }
        return undefined;
    }, [entityClasses, selectedEntity]);

    // The state to keep track of the currently selected numeric metric
    const [selectedNumericMetric, setSelectedNumericMetric] = useState<{
        readonly metric: ChartMetricInfo | undefined;
        readonly date: number | undefined;
    } | undefined>();


    // The state to keep track of the metrics to be highlighted. Whenever the `selectedNumericMetric` changed, we will need to update this state.
    const highlightedMetrics = useMemo((): MultiDataSourcesViewContainerProps['highlightedMetrics'] => {
        if (props.customSettings?.highlightSelectedMetrics && selectedNumericMetric?.metric) {
            return {
                numericMetrics: {
                    [selectedNumericMetric.metric.id]: true,
                },
            };
        }
        return undefined;
    }, [props.customSettings?.highlightSelectedMetrics, selectedNumericMetric?.metric]);


    /**
     * Go through all the entity's metricsDefinitions and for each metrics def, create 1 set of params that are needed to fetch data for the
     * collection associated with that metrics def. There should be 1 collection for each metrics definition.
     */
    const fetchCollectionsDataParams = useMemo((): readonly FetchCollectionDataParams[] | undefined => {
        if (selectedEntity && selectedEntityClass) {

            const numberOfColors = theme.palette.chart.series.length;
            // This metricsCounter is used for selecting color from the theme's palette
            let metricsCounter = 0;

            // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
            const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
                selectedEntityClass.metricsDefinitions ?? [], props.customSettings,
            );


            // Because 1 metricsDefinition is mapped to 1 collection so for each filteredMetricsDef, we will create 1
            // fetchCollectionDataParams
            const fetchCollectionDataParams = filteredMetricsDef.filter((metricDef) => {
                // Filter out all metric definitions that are NOT TSDB
                return metricDef.dataStoreConfiguration.moduleType === SmartModuleType.TSDB;
            }).map((metricDef): FetchCollectionDataParams => {

                // Filter out numeric metrics based on the customSettings' excludeNumericMetrics, excludeKPIMetrics, excludeRegularMetrics, etc...
                const filteredNumericMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId, metricDef.numericMetrics, props.customSettings, true,
                );

                // Filter out tag metrics based on the customSettings' excludeTagMetrics, excludeKPIMetrics, excludeRegularMetrics, etc...
                const filteredTaggedMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId, metricDef.tagMetrics, props.customSettings, false,
                );

                // Create the fetchCollectionDataParams
                const collectionId = getMetricsDefinitionMetricsId(selectedEntity, metricDef.metricsDefinitionId);
                return {
                    homeId        : selectedEntity.homeId,
                    smartModuleId : metricDef.dataStoreConfiguration.moduleInstanceId,
                    collectionId,
                    numericMetrics: filteredNumericMetrics.map((metric): FetchDataNumericMetricParams => {
                        const color = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].color : theme.palette.primary.dark;
                        const contrastText = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].contrastText : undefined;
                        metricsCounter += 1;

                        // Get the settings created SPECIFICALLY for the current metric if there is one. If not, see if the is a
                        // GLOBAL settings and use that if there is one.
                        const numericMetricsCustomSettings = props.customSettings?.numericMetricSettingsPerMetricsDefinition?.[metricDef.metricsDefinitionId]?.[metric.name]
                        ?? props.customSettings?.globalNumericMetricSettings;

                        const transformDataSettings = numericMetricsCustomSettings?.transform;

                        // If the "transformDataSettings" comes with custom "displayName" and "unit" then use them
                        const transformDataDisplayName = transformDataSettings?.displayNameKey ? trans(transformDataSettings.displayNameKey) : undefined;
                        const transformDataUnit = transformDataSettings?.unitKey ? trans(transformDataSettings.unitKey) : undefined;

                        return {
                            id               : createUniqueMetricId(collectionId, metric.name, true),
                            name             : metric.name,
                            displayName      : transformDataDisplayName ?? metric.evaluator?.displayName ?? metric.displayName ?? metric.name,
                            unit             : transformDataUnit ?? metric.unit,
                            decimalPlaces    : numericMetricsCustomSettings?.decimalPlaces,
                            range            : metric.range,
                            color,
                            contrastTextColor: contrastText,
                            transform        : transformDataSettings,
                            chartType        : numericMetricsCustomSettings?.chartType,
                            yAxisGroupName   : numericMetricsCustomSettings?.yAxisGroupName,
                        };
                    }),
                    tagMetrics: filteredTaggedMetrics.map((metric): FetchDataTagMetricParams => {
                        const color = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].color : theme.palette.primary.dark;
                        const contrastText = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].contrastText : undefined;
                        metricsCounter += 1;
                        return {
                            id               : createUniqueMetricId(collectionId, metric.name, false),
                            name             : metric.name,
                            displayName      : metric.displayName ?? metric.name,
                            enumDefinitions  : metric.enumDefinitions,
                            color,
                            contrastTextColor: contrastText,
                        };
                    }),
                    metricsDefinitionId    : metricDef.metricsDefinitionId,
                    metricDisplayNamePrefix: metricDef.displayName ?? metricDef.metricsDefinitionId,
                };
            });


            return fetchCollectionDataParams;
        }
        return undefined;
    }, [props.customSettings, selectedEntity, selectedEntityClass, theme.palette.chart.series, theme.palette.primary.dark, trans]);


    /**
     * Go through all the entity's metricsDefinitions and for each metrics def, create 1 set of params that are needed to fetch data for the
     * event logs associated with that metrics def. There should be 1 event log for each metrics definition.
     */
    const fetchEventLogsDataParams = useMemo((): readonly FetchEventLogDataParams[] | undefined => {
        if (selectedEntity && selectedEntityClass) {

            const numberOfColors = theme.palette.chart.series.length;

            // This metricsCounter is used for selecting color from the theme's palette
            let metricsCounter = 0;

            // filter out metrics definitions based on the customSettings' excludeMetricsDefinitionIds
            const filteredMetricsDef = filterMetricsDefinitionsByMetricsExclusionCustomSettings(
                selectedEntityClass.metricsDefinitions ?? [], props.customSettings,
            );


            const homeAppProxyMetricDefinitions = filteredMetricsDef.filter((metricDef) => {
                // Filter out all metric definitions that are NOT "home_app_proxy"
                return metricDef.dataStoreConfiguration.moduleType === EntityModuleType.HOME_APP_PROXY;
            }).filter((metricDef) => {
                // Filter out metricDefinition that don't have eventLogDataViewSettingsPerMetricsDefinition in the entity class customSettings.
                // We can't display "home_app_proxy" data without eventLogDataViewSettings.
                const eventLogDataViewSettings = props.customSettings?.eventLogDataViewSettingsPerMetricsDefinition?.[metricDef.metricsDefinitionId];
                if (!eventLogDataViewSettings) {
                    // log error so we know why this metric definition data doesn't show up
                    // eslint-disable-next-line no-console
                    console.info(`Missing eventLogDataViewSettings for metricDefinition id '${metricDef.metricsDefinitionId}'`);
                    return false;
                }
                return true;
            });


            // we will create 1 fetchEventLogDataParams for each metric definitions for "home_app_proxy" module type
            const fetchEventLogsDataParams = homeAppProxyMetricDefinitions.map((metricDef): FetchEventLogDataParams => {

                const eventLogDataViewSettings = props.customSettings?.eventLogDataViewSettingsPerMetricsDefinition?.[metricDef.metricsDefinitionId];

                // Filter out tag metrics based on the customSettings' excludeTagMetrics, excludeKPIMetrics, excludeRegularMetrics, etc...
                const filteredTaggedMetrics = filterMetricsByMetricsExclusionCustomSettings(
                    metricDef.metricsDefinitionId, metricDef.tagMetrics, props.customSettings, false,
                );

                // Create the FetchEventLogDataParams
                const searchKey = getMetricsDefinitionMetricsId(selectedEntity, metricDef.metricsDefinitionId);
                return {
                    homeId        : selectedEntity.homeId,
                    logSourcePath : metricDef.dataStoreConfiguration.moduleInstanceId,
                    searchKeys    : searchKey,
                    numericMetrics: [],
                    tagMetrics    : filteredTaggedMetrics.map((metric): FetchDataTagMetricParams => {
                        const color = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].color : theme.palette.primary.dark;
                        const contrastText = numberOfColors > 0 ? theme.palette.chart.series[metricsCounter % numberOfColors].contrastText : undefined;
                        metricsCounter += 1;
                        return {
                            id               : createUniqueMetricId(searchKey, metric.name, false),
                            name             : metric.name,
                            displayName      : metric.displayName ?? metric.name,
                            enumDefinitions  : metric.enumDefinitions,
                            color,
                            contrastTextColor: contrastText,
                        };
                    }),
                    metricDisplayNamePrefix : metricDef.metricsDefinitionId,
                    eventLogDataViewSettings: eventLogDataViewSettings as EventLogDataViewSettings,
                };
            });

            return fetchEventLogsDataParams;
        }
        return undefined;
    }, [props.customSettings, selectedEntity, selectedEntityClass, theme.palette.chart.series, theme.palette.primary.dark]);



    /**
     * Create the params that are required for fetching data. This will return FetchAggregatedDataOptions or FetchRawDataOptions
     * depending on the props.fetchDataParams.
     */
    const fetchDataOptions = useMemo((): FetchAggregatedDataOptions | FetchRawDataOptions => {

        if (isFetchEntityTimeRangeDataParams(props.fetchDataParams)) {
            if (props.customSettings?.aggregation === NoneValue) {
                return {
                    endTime  : props.fetchDataParams.endTime,
                    startTime: props.fetchDataParams.startTime,
                    limit    : props.customSettings.fetchRawDataLimit ?? MAX_RAW_DATA_COUNT,
                };
            }
            // Use the "aggregation" set in the component's customSettings first before using the one provided from the view
            return {
                aggregation: props.customSettings?.aggregation ?? props.fetchDataParams.aggregation,
                startTime  : props.fetchDataParams.startTime,
                endTime    : props.fetchDataParams.endTime,
            };
        }
        if (isFetchEntityTimeSpanDataParams(props.fetchDataParams)) {
            // If fetchDataParams is a TimeSpanDataParams which has 2 attributes, timestamps (array) and timeSpan, we will convert
            // it to FetchAggregatedDataOptions. We will use the FIRST timestamp in the timestamps array as startTime and add the timeSpan
            // to the startTime to get the endTime. For example, if timeSpan = "1 day", we add 24 hours to the startTime. If timeSpan = "1 week",
            // we add 7*24 hours to the startTime to get the endTime.
            const startTime = props.fetchDataParams.timestamps[0];
            const endTime = moment(startTime).add(1, props.fetchDataParams.timeSpan).valueOf();

            if (props.customSettings?.aggregation === NoneValue) {
                return {
                    endTime,
                    startTime,
                    limit: props.customSettings.fetchRawDataLimit ?? MAX_RAW_DATA_COUNT,
                };
            }

            return {
                aggregation: props.customSettings?.aggregation,
                startTime,
                endTime,
            };
        }


        // If fetchDataParams is not provided or not a known fetch data params then we will return an FetchRawDataOptions that fetches 500 data
        // points STARTING from the beginning of Today
        const now = moment().endOf('days');
        return {
            endTime  : now.valueOf(),
            startTime: moment(now).subtract(1, 'day').valueOf(),
            limit    : props.customSettings?.fetchRawDataLimit ?? MAX_RAW_DATA_COUNT,
        };
    }, [props.customSettings?.aggregation, props.customSettings?.fetchRawDataLimit, props.fetchDataParams]);



    // Get the adapter from the adapter collection and make sure it implement the correct adapter interface
    const adapter = useMemo(() => {
        if (props.customSettings?.adapter) {
            const adapter = EntityUIComponentAdapterCollection.getInstance().getAdapter(props.customSettings.adapter);
            if (isEntityCombinedMetricsViewUIComponentAdapter(adapter)) {
                return adapter;
            }
        }
        return undefined;
    }, [props.customSettings?.adapter]);



    /**
     * Handler for when a numeric metric is selected from the MultiDataSourcesViewContainer. We will save it in local state. It will be used
     * when we need to sort the metrics.
     */
    const onNumericMetricSelected = useCallback((metric: ChartMetricInfo | undefined, date: number) => {
        setSelectedNumericMetric({ metric, date });
    }, []);


    /**
     * Handler for when a numeric metric is unselected from the MultiDataSourcesViewContainer. We will clear it in local state.
     */
    const onClearSelectedNumericMetric = useCallback(() => {
        setSelectedNumericMetric(undefined);
    }, []);


    /**
     * Create the param data for the adapter and cache it. We will pass these to the adapter function when we call them
     */
    const adapterDefaultParams = useMemo((): EntityCombinedMetricsViewUIComponentAdapterDefaultParams | undefined => {
        if (selectedEntity && selectedEntityClass) {
            return {
                entity               : selectedEntity,
                entityClass          : selectedEntityClass,
                selectedNumericMetric: selectedNumericMetric?.metric,
                selectedDate         : selectedNumericMetric?.date,
                customSettings       : props.customSettings,
                trans,
                theme,
            };
        }
        return undefined;
    }, [props.customSettings, selectedEntity, selectedEntityClass, selectedNumericMetric?.date, selectedNumericMetric?.metric, theme, trans]);



    /**
     * Handler for sorting tagMetrics called by the MultiDataSourcesViewContainer. This function will not sort the metrics. It will check if
     * the adapter has a handler for sorting the metrics and call the adapter to do it instead. If the plug in does not support this, it will
     * just return the same metrics.
     *
     * NOTE: This is a proxy because the MultiDataSourcesViewContainer will not call the adapter function directly. It will call this
     * function and then this function will call the adapter's function because we need to pass additional info to the adapter incase the adapter
     * needs those info
     */
    const tagMetricsSorterProxy = useCallback((
        tagMetrics: readonly ChartMetricInfo[],
        ganttChartDataPoints: {
            [metricId: string]: GanttChartDataPoint[] | undefined;
        } | undefined,
    ): readonly ChartMetricInfo[] => {
        // If there is a handler for sorting tagMetrics then call it and return the result from that function.
        if (adapterDefaultParams && adapter?.sortTagMetrics) {
            // Calling an adapter function can fail because we don't know if it does the right thing so we need to try/catch
            try {
                const sortedMetrics = adapter.sortTagMetrics({
                    ...adapterDefaultParams,
                    tagMetrics,
                    ganttChartDataPoints,
                });

                return sortedMetrics;
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }

        // Else return the same metrics passed to this function
        return tagMetrics;
    }, [adapter, adapterDefaultParams]);


    const convertEventLogRecordsToTagChartDataProxy = useCallback((
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    ): {
        [metricId: string]: GanttChartDataPoint[];
    } => {
        if (adapter?.convertEventLogRecordsToTagChartData) {
            return adapter.convertEventLogRecordsToTagChartData({
                eventLogData,
                eventLogInfo,
            });
        }
        return {};
    }, [adapter]);


    const convertEventLogRecordsToTagMetricsProxy = useCallback((
        eventLogData: EventLogData,
        eventLogInfo: FetchEventLogDataParams,
    ): readonly ChartMetricInfo[] => {
        if (adapter?.convertEventLogRecordsToTagMetrics) {
            return adapter.convertEventLogRecordsToTagMetrics({
                eventLogData,
                eventLogInfo,
            });
        }
        return [];
    }, [adapter]);


    /**
     * Handler for creating Y Axis label in HTML format for the MultiDataSourcesViewContainer's numeric chart. This will just forward the call
     * to the adapter.
     */
    const getNumericMetricChartYAxisHTMLLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, metric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getNumericMetricChartYAxisHTMLLabel) {
            try {
                return adapter.getNumericMetricChartYAxisHTMLLabel?.({
                    ...adapterDefaultParams,
                    text,
                    targetAxis,
                    targetMetric: metric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for creating Y Axis label in TEXT format for the MultiDataSourcesViewContainer's numeric chart. This will just forward the call
     * to the adapter.
     */
    const getNumericMetricChartYAxisTextLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getNumericMetricChartYAxisTextLabel) {
            try {
                return adapter.getNumericMetricChartYAxisTextLabel?.({
                    ...adapterDefaultParams,
                    text,
                    targetAxis,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);



    /**
     * Handler for creating Y Axis label in HTML format for the MultiDataSourcesViewContainer's tag chart. This will just forward the call
     * to the adapter.
     */
    const getTagMetricChartYAxisHTMLLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && selectedEntity && selectedEntityClass && adapter?.getTagMetricChartYAxisHTMLLabel) {
            try {
                return adapter.getTagMetricChartYAxisHTMLLabel?.({
                    ...adapterDefaultParams,
                    text,
                    targetAxis,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams, selectedEntity, selectedEntityClass]);


    /**
     * Handler for creating Y Axis label in TEXT format for the MultiDataSourcesViewContainer's tag chart. This will just forward the call
     * to the adapter.
     */
    const getTagMetricChartYAxisTextLabelProxy = useCallback((
        text: string | undefined, targetAxis: am4charts.AxisLabel, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getTagMetricChartYAxisTextLabel) {
            try {
                return adapter.getTagMetricChartYAxisTextLabel?.({
                    ...adapterDefaultParams,
                    text,
                    targetAxis,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);



    /**
     * Handler for creating Tooltip TEXT for the MultiDataSourcesViewContainer's numeric chart. This will just forward the call
     * to the adapter.
     */
    const getNumericMetricChartTooltipTextProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getNumericMetricChartTooltipText) {
            try {
                return adapter.getNumericMetricChartTooltipText?.({
                    ...adapterDefaultParams,
                    text,
                    targetSeries,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for creating Tooltip HTML for the MultiDataSourcesViewContainer's numeric chart. This will just forward the call
     * to the adapter.
     */
    const getNumericMetricChartTooltipHTMLProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getNumericMetricChartTooltipHTML) {
            try {
                return adapter.getNumericMetricChartTooltipHTML?.({
                    ...adapterDefaultParams,
                    text,
                    targetSeries,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for creating Tooltip TEXT for the MultiDataSourcesViewContainer's TAG chart. This will just forward the call
     * to the adapter.
     */
    const getTagMetricChartTooltipTextProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getTagMetricChartTooltipText) {
            try {
                return adapter.getTagMetricChartTooltipText?.({
                    ...adapterDefaultParams,
                    text,
                    targetSeries,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for creating Tooltip HTML for the MultiDataSourcesViewContainer's TAG chart. This will just forward the call
     * to the adapter.
     */
    const getTagMetricChartTooltipHTMLProxy = useCallback((
        text: string | undefined, targetSeries: am4charts.XYSeries, targetMetric: ChartMetricInfo,
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.getTagMetricChartTooltipHTML) {
            try {
                return adapter.getTagMetricChartTooltipHTML?.({
                    ...adapterDefaultParams,
                    text,
                    targetSeries,
                    targetMetric,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return text;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for returning the list of export data format the handler supports. This will just forward the call to the adapter.
     */
    const getSupportedExportDataFormatsProxy = useCallback((
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): ReadonlyNonEmptyArray<ExportDataFormat> | undefined => {
        if (adapterDefaultParams && adapter?.getSupportedExportDataFormats) {
            try {
                return adapter.getSupportedExportDataFormats?.({
                    ...adapterDefaultParams,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return undefined;
    }, [adapter, adapterDefaultParams]);


    /**
     * Handler for creating the file content for the data to be exported. This will just forward the call to the adapter.
     */
    const createExportFileContentProxy = useCallback((
        selectedDataFormat: string, selectedNumericMetrics: readonly ChartMetricInfo[], selectedTagMetrics: readonly ChartMetricInfo[],
        numericMetrics: readonly ChartMetricInfo[],
        tagMetrics: readonly ChartMetricInfo[],
        metricsDataPoints: readonly MultiMetricsDataPoint[] | undefined,
        ganttChartDataPoints: {
            [metricId: string]: readonly GanttChartDataPoint[] | undefined;
        } | undefined,
    ): string | undefined => {
        if (adapterDefaultParams && adapter?.createExportFileContent) {
            try {
                return adapter.createExportFileContent?.({
                    ...adapterDefaultParams,
                    selectedDataFormat,
                    selectedNumericMetrics,
                    selectedTagMetrics,
                    numericMetrics,
                    tagMetrics,
                    metricsDataPoints,
                    ganttChartDataPoints,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error(error);
            }
        }
        return undefined;
    }, [adapter, adapterDefaultParams]);



    /**
     * If "customSettings.preselectMetricsPerMetricsDefinition" contain the list of metric names to be preselected then create a Map of selected
     * metrics to pass to the MultiDataSourcesViewContainer.
     *
     * In the `preselectMetricsPerMetricsDefinition` settings, the admin uses simple metric names e.g. "temperature", "pressure", etc....
     * However internally, these are not the name the component uses. The component needs to convert these names into something more unique by
     * concatenating the `entityClass + metricsDefinitionId + metricName`. We call them metricId. Therefore this function is for converting
     * simple metric names to unique metric name. This function will convert this object
     *
     * From this:
     *
     *      preselectMetricsPerMetricsDefinition: {
     *          environment: {
     *              numericMetrics: ["temperature", "pressure", ...]
     *              tagMetrics: ["error", "state", ...]
     *          }
     *      }
     *
     * To this:
     *      preselectMetricsPerMetricsDefinition: {
     *          numericMetrics: ["my_entity_1::environment::temperature", "my_entity_1::environment::pressure", ...]
     *          tagMetrics: ["my_entity_1::environment::error", "my_entity_1::environment::state", ...]
     *      }
     *
     */
    const initialSelectedMetricIdsMap = useMemo((): MultiDataSourcesViewContainerProps['initialSelectedMetricIdsMap'] => {

        if (selectedEntity && props.customSettings?.preselectMetricsPerMetricsDefinition) {
            // Create a map of selected numeric and tag metrics and then loop through the list of metric names to be selected and populate the map data
            const selectedNumericMetrics: {
                [metricId: string]: boolean | undefined;
            } = {};

            const selectedTagMetrics: {
                [metricId: string]: boolean | undefined;
            } = {};

            Object.entries(props.customSettings?.preselectMetricsPerMetricsDefinition).forEach(([metricsDefId, selectedMetricsSettings]) => {
                const collectionId = getMetricsDefinitionMetricsId(selectedEntity, metricsDefId);

                selectedMetricsSettings?.numericMetrics?.forEach((metricName) => {
                    const metricId = createUniqueMetricId(collectionId, metricName, true);
                    selectedNumericMetrics[metricId] = true;
                });
                selectedMetricsSettings?.tagMetrics?.forEach((metricName) => {
                    const metricId = createUniqueMetricId(collectionId, metricName, false);
                    selectedTagMetrics[metricId] = true;
                });
            });

            return {
                numericMetrics: selectedNumericMetrics,
                tagMetrics    : selectedTagMetrics,
            };
        }

        return undefined;
    }, [props.customSettings?.preselectMetricsPerMetricsDefinition, selectedEntity]);


    /**
     * "customSettings?.chartViewSettings" contains strings that might need to be translated therefore, we need to translate
     * it before passing it to the MultiDataSourcesViewContainer
     */
    const localizedChartViewSettings = useMemo((): ChartViewSettings | undefined => {
        const chartViewSettings = props.customSettings?.chartViewSettings;
        if (chartViewSettings) {

            const xBoxes = chartViewSettings.numericChartSettings?.xAxisBoxes;
            const yBoxes = chartViewSettings.numericChartSettings?.yAxisBoxes;
            const localizedXBoxes = xBoxes?.map((box) => {
                return {
                    ...box,
                    label: box.label
                        ? {
                            ...box.label,
                            text: trans(box.label.text),
                        }
                        : undefined,
                };
            });
            const localizedYBoxes = yBoxes?.map((box) => {
                return {
                    ...box,
                    label: box.label
                        ? {
                            ...box.label,
                            text: trans(box.label.text),
                        }
                        : undefined,
                };
            });

            return {
                ...props.customSettings.chartViewSettings,
                numericChartSettings: {
                    ...chartViewSettings.numericChartSettings,
                    xAxisBoxes: localizedXBoxes,
                    yAxisBoxes: localizedYBoxes,
                },
            };
        }
        return undefined;
    }, [props.customSettings?.chartViewSettings, trans]);

    return (
        <>
            {fetchCollectionsDataParams && fetchCollectionsDataParams.length > 0 && (
                <MultiDataSourcesViewContainer
                    customStyles={props.customSettings?.styles}
                    projectId={props.projectId}
                    chartIdPrefix={componentIdRef.current}
                    fetchCollectionsDataParams={fetchCollectionsDataParams}
                    fetchEventLogDataParams={fetchEventLogsDataParams}
                    fetchDataOptions={fetchDataOptions}
                    initialSelectedMetricIdsMap={initialSelectedMetricIdsMap}
                    onNumericMetricSelected={onNumericMetricSelected}
                    onClearSelectedNumericMetric={onClearSelectedNumericMetric}
                    icon={props.customSettings?.icon}
                    title={props.customSettings?.titleKey ? trans(props.customSettings.titleKey) : props.customSettings?.title}
                    chartViewSettings={localizedChartViewSettings}
                    tableViewSettings={props.customSettings?.tableViewSettings}
                    showChartView={props.customSettings?.showChartView}
                    showTableView={props.customSettings?.showTableView}
                    metricViewSettings={props.customSettings?.metricViewSettings}
                    showVerticalLineAtTimestamp={props.customSettings?.displayVerticalLineWhereClicked ? selectedNumericMetric?.date : undefined}
                    highlightedMetrics={props.customSettings?.highlightSelectedMetrics ? highlightedMetrics : undefined}
                    convertEventLogRecordsToTagChartData={adapter?.convertEventLogRecordsToTagChartData ? convertEventLogRecordsToTagChartDataProxy : undefined}
                    convertEventLogRecordsToTagMetrics={adapter?.convertEventLogRecordsToTagMetrics ? convertEventLogRecordsToTagMetricsProxy : undefined}
                    tagMetricsSorter={adapter?.sortTagMetrics ? tagMetricsSorterProxy : undefined}
                    getNumericMetricChartYAxisHTMLLabel={adapter?.getNumericMetricChartYAxisHTMLLabel ? getNumericMetricChartYAxisHTMLLabelProxy : undefined}
                    getNumericMetricChartYAxisTextLabel={adapter?.getNumericMetricChartYAxisTextLabel ? getNumericMetricChartYAxisTextLabelProxy : undefined}
                    getTagMetricChartYAxisHTMLLabel={adapter?.getTagMetricChartYAxisHTMLLabel ? getTagMetricChartYAxisHTMLLabelProxy : undefined}
                    getTagMetricChartYAxisTextLabel={adapter?.getTagMetricChartYAxisTextLabel ? getTagMetricChartYAxisTextLabelProxy : undefined}
                    getNumericMetricChartTooltipText={adapter?.getNumericMetricChartTooltipText ? getNumericMetricChartTooltipTextProxy : undefined}
                    getNumericMetricChartTooltipHTML={adapter?.getNumericMetricChartTooltipHTML ? getNumericMetricChartTooltipHTMLProxy : undefined}
                    getTagMetricChartTooltipText={adapter?.getTagMetricChartTooltipText ? getTagMetricChartTooltipTextProxy : undefined}
                    getTagMetricChartTooltipHTML={adapter?.getTagMetricChartTooltipHTML ? getTagMetricChartTooltipHTMLProxy : undefined}
                    getSupportedExportDataFormats={adapter?.getSupportedExportDataFormats ? getSupportedExportDataFormatsProxy : undefined}
                    createExportFileContent={adapter?.createExportFileContent ? createExportFileContentProxy : undefined}
                />
            )}

            {!fetchCollectionsDataParams && (
                <CircularProgress />
            )}
        </>
    );
};



// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityCombinedMetricsViewUIComponent', EntityCombinedMetricsViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Combined_Metrics_Comp',
    {
        componentFunc          : EntityCombinedMetricsViewUIComponent,
        customSettingsValidator: isEntityCombinedMetricsViewUIComponentCustomSettings,
    },
);
